// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Button,
  Card,
  Flex,
  JanePayLogo,
  Typography,
} from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';

import { useKioskSpacing } from '../../../hooks';
import { SCREEN_NAMES, useCheckout } from '../../../providers';
import { ButtonWrapper } from '../Checkout/checkout.styles';
import {
  CheckoutFlowButton,
  SubmitCustomerInfoButton,
} from '../CustomerInfoFields/CustomerInfoFields';
import { PaymentOptionCard } from './PaymentOptionCard';

const PaymentButton = styled(Card.Content)<{ isIpad: boolean }>(
  ({ isIpad }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: isIpad ? '72px' : '96px',
  })
);

export const PaymentSelectionScreen = () => {
  const { defaultSpacing, isIpad, getKioskSpacing } = useKioskSpacing();
  const showAchFees = useFlag(FLAGS.c3AchFeesOnKiosk);

  const {
    cart,
    bankAccounts,
    isOrderLoading,
    handleContinueToMFAClick,
    handleLinkBankClick,
    handleSubmitWithValidation,
    janePayCartLoading,
    janePayOrderTotal,
    janePayServiceFeeAmount,
    selectedPayment,
    setScreen,
    setSelectedPayment,
    store,
  } = useCheckout();

  const PAYMENT_OPTIONS = {
    link: showAchFees
      ? 'Link your bank account'
      : 'Pay by linking your bank account',
    cash: 'Pay at cashier',
  };

  const whiteLabel = store?.jane_pay_integration?.white_label;
  const cashBack = cart?.brand_discounts_total;

  const defaultSelectionText = showAchFees
    ? 'Select your payment method. Pay now with JanePay for a fast, secure checkout, or pay at the counter.'
    : 'Select your payment method and place your order.';
  const selectionText = cashBack
    ? `Select your payment method and place your order. Your estimated Jane
    Gold cash back of ${formatCurrency(
      cashBack
    )} will be deposited to your linked bank account in
    1-3 days.`
    : defaultSelectionText;
  const hasBankAccount = bankAccounts?.length > 0;

  const showLinkBankButton = selectedPayment === 'link';
  const showContinueButton = bankAccounts.find(
    (account) => account.account_last_4 === selectedPayment
  );

  const showCashbackBadge = (cashBack ?? 0) > 0 && !hasBankAccount;

  const linkOptions = hasBankAccount
    ? bankAccounts.map((account) => ({
        id: account.account_last_4,
        name: account.name,
        paymentValue: account.account_last_4,
      }))
    : [{ id: 'link', name: PAYMENT_OPTIONS.link, paymentValue: 'link' }];

  const cancelLabel = showAchFees ? 'Cancel' : 'Back';
  const linkBankLabel = showAchFees ? 'Link Bank' : 'Link bank';
  const headingLabel = showAchFees ? 'Select payment method' : 'Pay now with';

  return (
    <Flex flexDirection="column">
      <Typography>{selectionText}</Typography>
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={16}
        py={defaultSpacing}
        aria-label={`Payment options, selected option: ${
          selectedPayment === 'link' || selectedPayment === 'cash'
            ? PAYMENT_OPTIONS[selectedPayment as keyof typeof PAYMENT_OPTIONS]
            : `account number ending with ${selectedPayment}`
        }`}
      >
        <Typography variant="caps-bold" textAlign="center">
          {headingLabel}
        </Typography>
        {showAchFees ? (
          linkOptions.map((option) => (
            <PaymentOptionCard
              key={option.id}
              name={option.name}
              onClick={() => setSelectedPayment(option.paymentValue)}
              selected={selectedPayment === option.paymentValue}
              showJanePayLogo={!whiteLabel}
              total={janePayOrderTotal}
              feeAmount={janePayServiceFeeAmount}
              cashBack={cashBack}
              showCashbackBadge={showCashbackBadge}
            />
          ))
        ) : hasBankAccount ? (
          <>
            {bankAccounts.map((account) => (
              <Card
                border={
                  selectedPayment === account.account_last_4
                    ? 'primary'
                    : 'transparent-black-ten'
                }
                borderWidth={
                  selectedPayment === account.account_last_4 ? '2px' : '1px'
                }
                width="100%"
                flat
                key={account.account_last_4}
              >
                <PaymentButton
                  ariaLabel={`Select ${account.name}`}
                  onClick={() => setSelectedPayment(account.account_last_4)}
                  isIpad={isIpad}
                >
                  {!whiteLabel && (
                    <JanePayLogo height={20} data-testid="jane-pay-logo" />
                  )}
                  <Typography textAlign="center">{account.name}</Typography>
                </PaymentButton>
              </Card>
            ))}
          </>
        ) : (
          <Card
            border={
              selectedPayment === 'link' ? 'primary' : 'transparent-black-ten'
            }
            borderWidth={selectedPayment === 'link' ? '2px' : '1px'}
            width="100%"
            flat
          >
            <PaymentButton
              ariaLabel={`Select ${PAYMENT_OPTIONS.link}`}
              onClick={() => setSelectedPayment('link')}
              isIpad={isIpad}
            >
              {!whiteLabel && (
                <JanePayLogo height={20} data-testid="jane-pay-logo" />
              )}
              <Typography textAlign="center">{PAYMENT_OPTIONS.link}</Typography>
            </PaymentButton>
          </Card>
        )}
        {showAchFees ? (
          <PaymentOptionCard
            name={PAYMENT_OPTIONS.cash}
            onClick={() => setSelectedPayment('cash')}
            selected={selectedPayment === 'cash'}
            total={cart?.order_total ?? 0}
            subLabel="Store fees vary"
          />
        ) : (
          <>
            <Typography variant="caps" color="grays-mid" textAlign="center">
              Or
            </Typography>
            <Card
              border={
                selectedPayment === 'cash' ? 'primary' : 'transparent-black-ten'
              }
              borderWidth={selectedPayment === 'cash' ? '2px' : '1px'}
              width="100%"
              flat
            >
              <PaymentButton
                onClick={() => setSelectedPayment('cash')}
                ariaLabel={`Select ${PAYMENT_OPTIONS.cash}`}
                isIpad={isIpad}
              >
                <Typography textAlign="center">
                  {PAYMENT_OPTIONS.cash}
                </Typography>
              </PaymentButton>
            </Card>
          </>
        )}
        <Typography variant="mini" color="grays-mid" textAlign="center">
          For your privacy, you will be signed out automatically after placing
          your order.
        </Typography>
      </Flex>

      <ButtonWrapper
        marginTop={defaultSpacing}
        gap={getKioskSpacing(24, 16)}
        isIpad={isIpad}
      >
        <Button
          label={
            isIpad ? (
              cancelLabel
            ) : (
              <Typography as="h3" variant="section-bold">
                {cancelLabel}
              </Typography>
            )
          }
          onClick={() => setScreen(SCREEN_NAMES.customerInfo)}
          size={isIpad ? 'small' : 'large'}
          full
          variant="tertiary"
          disabled={isOrderLoading}
        />

        {showLinkBankButton ? (
          <CheckoutFlowButton
            ariaLabel="link your bank account to pay"
            buttonLabel={linkBankLabel}
            loading={isOrderLoading}
            size={isIpad ? 'small' : 'large'}
            onClick={handleLinkBankClick}
          />
        ) : showContinueButton ? (
          <CheckoutFlowButton
            ariaLabel="continue to authenticate your phone number and submit order"
            buttonLabel="Continue"
            loading={isOrderLoading || janePayCartLoading}
            size={isIpad ? 'small' : 'large'}
            onClick={handleContinueToMFAClick}
          />
        ) : (
          <SubmitCustomerInfoButton
            loading={isOrderLoading}
            size={isIpad ? 'small' : 'large'}
            onClick={handleSubmitWithValidation}
          />
        )}
      </ButtonWrapper>
    </Flex>
  );
};
